import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const AuthContext = createContext(null);
const KEY = 'iw_admin_tokens';

const readTokens = () => {
  try { return JSON.parse(localStorage.getItem(KEY)) || null; } catch { return null; }
};
const writeTokens = (t) => (t ? localStorage.setItem(KEY, JSON.stringify(t)) : localStorage.removeItem(KEY));
const applyHeader = (access) => {
  if (access) axios.defaults.headers.common.Authorization = `Bearer ${access}`;
  else delete axios.defaults.headers.common.Authorization;
};

export const formatApiErrorDetail = (detail) => {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null); // null = checking, false = logged out

  const logout = useCallback(() => {
    writeTokens(null);
    applyHeader(null);
    setUser(false);
  }, []);

  const refresh = useCallback(async () => {
    const tokens = readTokens();
    if (!tokens?.refresh_token) throw new Error('no refresh token');
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/refresh`, { refresh_token: tokens.refresh_token }, { headers: { Authorization: '' } });
    const next = { ...tokens, access_token: data.access_token };
    writeTokens(next);
    applyHeader(next.access_token);
    return next.access_token;
  }, []);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      async (error) => {
        const original = error.config;
        const isAuthCall = original?.url?.includes('/api/auth/');
        if (error.response?.status === 401 && !original._retry && !isAuthCall && readTokens()) {
          original._retry = true;
          try {
            const access = await refresh();
            original.headers.Authorization = `Bearer ${access}`;
            return axios(original);
          } catch {
            logout();
          }
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [refresh, logout]);

  useEffect(() => {
    const tokens = readTokens();
    if (!tokens) { setUser(false); return; }
    applyHeader(tokens.access_token);
    axios.get(`${BACKEND_URL}/api/auth/me`)
      .then((r) => setUser(r.data))
      .catch(() => logout());
  }, [logout]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${BACKEND_URL}/api/auth/login`, { email, password });
    writeTokens({ access_token: data.access_token, refresh_token: data.refresh_token });
    applyHeader(data.access_token);
    setUser(data.user);
    return data.user;
  };

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
